import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import {
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { SectionPaper, SectionTitle } from './styledComponents';
import { supabase } from '../../../../supabaseClient';

const OriginDestinationSection = ({ bol, onUpdate, purchaseOrder, company, canEdit = true }) => {
  const theme = useTheme();

  const autofillShipper = () => {
    if (company) {
      onUpdate({
        shipper_name: company.name || '',
        shipper_contact: company.phone || '',
        shipper_email: company.email || '',
        origin_street: company.address || '',
        origin_city: company.city || '',
        origin_state: company.state || '',
        origin_zip: company.zip_code || '',
        origin_country: company.country || 'United States'
      });
    }
  };

  const autofillConsignee = async () => {
    if (!purchaseOrder) return;

    let updates = {
      consignee_name: purchaseOrder.customer_name || '',
      consignee_company: purchaseOrder.customer_company || '',
      destination_street: purchaseOrder.shipping_street || purchaseOrder.billing_street || '',
      destination_city: purchaseOrder.shipping_city || purchaseOrder.billing_city || '',
      destination_state: purchaseOrder.shipping_state || purchaseOrder.billing_state || '',
      destination_zip: purchaseOrder.shipping_zip || purchaseOrder.billing_zip || '',
      destination_country: purchaseOrder.shipping_country || purchaseOrder.billing_country || ''
    };

    // If no address in proforma invoice, fetch from customer table
    if (!updates.destination_street && purchaseOrder.customer_id) {
      try {
        const { data: customer, error } = await supabase
          .from('customers')
          .select('name, company, street, city, state, zip_code, country')
          .eq('id', purchaseOrder.customer_id)
          .single();

        if (!error && customer) {
          updates = {
            consignee_name: customer.name || purchaseOrder.customer_name || '',
            consignee_company: customer.company || purchaseOrder.customer_company || '',
            destination_street: customer.street || '',
            destination_city: customer.city || '',
            destination_state: customer.state || '',
            destination_zip: customer.zip_code || '',
            destination_country: customer.country || ''
          };
        }
      } catch (err) {
        console.error('Error fetching customer address:', err);
      }
    }

    onUpdate(updates);
  };

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6">
        <LocationIcon />
        Origin & Destination
      </SectionTitle>

      <Grid container spacing={4}>
        {/* Shipper/Origin Information */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>
              Shipper (Consignor)
            </Typography>
            <Button
              size="small"
              onClick={() => canEdit && autofillShipper()}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                fontWeight: 500,
                fontSize: '0.75rem',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Auto-fill from Company
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Shipper Name"
                fullWidth
                value={bol.shipper_name || ''}
                onChange={(e) => canEdit && onUpdate({ shipper_name: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Phone"
                fullWidth
                value={bol.shipper_contact || ''}
                onChange={(e) => canEdit && onUpdate({ shipper_contact: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Email"
                fullWidth
                value={bol.shipper_email || ''}
                onChange={(e) => canEdit && onUpdate({ shipper_email: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 1 }}>
                Origin Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Street"
                fullWidth
                value={bol.origin_street || ''}
                onChange={(e) => canEdit && onUpdate({ origin_street: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={bol.origin_city || ''}
                onChange={(e) => canEdit && onUpdate({ origin_city: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                fullWidth
                value={bol.origin_state || ''}
                onChange={(e) => canEdit && onUpdate({ origin_state: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Zip"
                fullWidth
                value={bol.origin_zip || ''}
                onChange={(e) => canEdit && onUpdate({ origin_zip: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                fullWidth
                value={bol.origin_country || ''}
                onChange={(e) => canEdit && onUpdate({ origin_country: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pickup Location (Optional)"
                fullWidth
                multiline
                rows={2}
                value={bol.pickup_location || ''}
                onChange={(e) => canEdit && onUpdate({ pickup_location: e.target.value })}
                placeholder="Specific pickup location or instructions..."
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Consignee/Destination Information */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '1rem', color: 'primary.main' }}>
              Consignee (Receiver)
            </Typography>
            <Button
              size="small"
              onClick={() => canEdit && autofillConsignee()}
              sx={{ 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                fontWeight: 500,
                fontSize: '0.75rem',
                opacity: canEdit ? 1 : 0.5,
                pointerEvents: canEdit ? 'auto' : 'none',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              Auto-fill from Customer
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Consignee Name"
                fullWidth
                value={bol.consignee_name || ''}
                onChange={(e) => canEdit && onUpdate({ consignee_name: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Company Name (Optional)"
                fullWidth
                value={bol.consignee_company || ''}
                onChange={(e) => canEdit && onUpdate({ consignee_company: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block', mb: 1 }}>
                Destination Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Street"
                fullWidth
                value={bol.destination_street || ''}
                onChange={(e) => canEdit && onUpdate({ destination_street: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={bol.destination_city || ''}
                onChange={(e) => canEdit && onUpdate({ destination_city: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="State"
                fullWidth
                value={bol.destination_state || ''}
                onChange={(e) => canEdit && onUpdate({ destination_state: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Zip"
                fullWidth
                value={bol.destination_zip || ''}
                onChange={(e) => canEdit && onUpdate({ destination_zip: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                fullWidth
                value={bol.destination_country || ''}
                onChange={(e) => canEdit && onUpdate({ destination_country: e.target.value })}
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Delivery Location (Optional)"
                fullWidth
                multiline
                rows={2}
                value={bol.delivery_location || ''}
                onChange={(e) => canEdit && onUpdate({ delivery_location: e.target.value })}
                placeholder="Specific delivery location or instructions..."
                InputProps={{
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '& fieldset': { border: 'none' },
                    opacity: canEdit ? 1 : 0.5,
                    pointerEvents: canEdit ? 'auto' : 'none',
                    cursor: canEdit ? 'text' : 'not-allowed'
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default OriginDestinationSection;